import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import {
  Buildings,
  Shield,
  Elevator,
  Certificate,
  CaretDown,
  CaretUp,
  ClipboardText,
  NoteBlank,
  Lightning,
  Envelope,
  UsersThree,
  FireExtinguisher,
  Gear,
} from '@phosphor-icons/react';
import TaskDetailsDialog from '../components/TaskDetailsDialog';
import OperationsWidget from '../components/OperationsWidget';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const formatDateDMY = (d) => {
  if (!d) return '';
  const p = d.split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d;
};

export default function Dashboard() {
  const { t, language: lang } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [bldgData, setBldgData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [notes, setNotes] = useState([]);
  // Phase 600 — Open the shared Task Details dialog directly from
  // Dashboard. No more building-page redirect. Same source of truth
  // (`/api/tasks/{id}`) keeps Dashboard + per-building views in sync.
  const [taskDetailsOpen, setTaskDetailsOpen] = useState(false);
  const [taskDetailsTask, setTaskDetailsTask] = useState(null);
  const openTaskDetails = (tk) => { setTaskDetailsTask(tk); setTaskDetailsOpen(true); };

  // Filters
  const [alertTypeFilter] = useState('all');

  // Phase 831 — Aggressive refetch policy so the Dashboard NEVER
  // displays stale data after a building/expense/invoice/payment is
  // deleted elsewhere. We refetch on:
  //   1. Component mount (initial load).
  //   2. React Router navigation into /dashboard (via location.key).
  //   3. Browser tab regains focus (window `focus` + Page Visibility API).
  //   4. Cross-tab storage signal (localStorage 'dashboard:invalidate').
  const location = useLocation();
  useEffect(() => { fetchData(); }, [location.key]);
  useEffect(() => {
    const refresh = () => { if (document.visibilityState === 'visible') fetchData(); };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    const onStorage = (e) => { if (e.key === 'dashboard:invalidate') fetchData(); };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const [nextMeetings, setNextMeetings] = useState([]);
  // Phase 204 — Payments coverage per building
  const [coverage, setCoverage] = useState({ buildings: [], today: '', categories: {} });
  // Phase 951 — Active category CODE (201-206 monthly, 301-310 capital)
  const [coverageCategory, setCoverageCategory] = useState('201');
  const [coverageSort, setCoverageSort] = useState('oldest'); // 'oldest' | 'unpaid'
  // Phase 951 — User-configurable Payments-by-Category tabs by expense
  // category code. Persisted per browser in localStorage. Default is
  // Management + Cleaning (201, 202). User can add ANY monthly (201-206)
  // or capital (301-310) code — no "Other" bucket exists.
  const [pbcVisibleCats, setPbcVisibleCats] = useState(() => {
    try {
      const raw = localStorage.getItem('dashboard.paymentsByCategory.visibleCodes');
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) return arr;
      }
    } catch { /* ignore */ }
    return ['201', '202'];
  });
  const [pbcSettingsOpen, setPbcSettingsOpen] = useState(false);
  useEffect(() => {
    try {
      localStorage.setItem('dashboard.paymentsByCategory.visibleCodes', JSON.stringify(pbcVisibleCats));
    } catch { /* ignore */ }
  }, [pbcVisibleCats]);

  // Phase 810 — Operations Dashboard aggregate (Electricity / Insurance /
  // Certifications / Emails / Next Meetings).
  const [ops, setOps] = useState({ electricity: [], insurance: [], certifications: [], emails: [], next_meetings: [] });

  // Phase 989 — Email Center filter (Building Management dashboard):
  // free-text search + explicit building dropdown. Applied to the
  // `ops.emails` rows only, never mutates the underlying data.
  const [emailFilter, setEmailFilter] = useState({ q: '', building_id: '' });

  const fetchData = async () => {
    try {
      const [statsRes, bldgRes, tasksRes, notesRes, nextMtgRes, coverageRes, opsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API}/dashboard/building-management`, { withCredentials: true }),
        axios.get(`${API}/tasks`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/notes`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/meetings/next-meetings`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/dashboard/payments-coverage`, { withCredentials: true }).catch(() => ({ data: { buildings: [], categories: {} } })),
        axios.get(`${API}/dashboard/operations`, { withCredentials: true }).catch(() => ({ data: {} })),
      ]);
      setStats(statsRes.data);
      setBldgData(bldgRes.data);
      setTasks(tasksRes.data || []);
      setNotes(notesRes.data || []);
      setNextMeetings(nextMtgRes.data || []);
      setCoverage(coverageRes.data || { buildings: [], categories: {} });
      setOps({
        electricity: opsRes.data?.electricity || [],
        insurance: opsRes.data?.insurance || [],
        certifications: opsRes.data?.certifications || [],
        emails: opsRes.data?.emails || [],
        next_meetings: opsRes.data?.next_meetings || [],
      });
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const aptDebts = bldgData?.apartment_debts || [];
  const alerts = bldgData?.alerts || [];

  const filteredAlerts = alerts.filter(a => {
    if (alertTypeFilter !== 'all' && a.type !== alertTypeFilter) return false;
    return true;
  });

  // Phase 209o — Type dropdown shows ONLY real alert categories (true types).
  // "Unpaid" is a STATUS, not a type — moved out of this dropdown. A separate
  // status filter may be added in the future.
  const alertTypeOptions = [
    { value: 'all', label: t('allTypes') },
    { value: 'insurance', label: t('insurance') },
    { value: 'elevator_certification', label: t('elevatorCertification') },
    { value: 'elevator_contract', label: t('elevatorContract') },
  ];

  // Phase 209s — All alert-derived stats (Active Alerts card, critical/
  // warning badges, list header) MUST mirror the same filtered data set so
  // the "Active Alerts" widget at the top stays in lock-step with the
  // Alerts & Expirations list below. When the user changes the type filter,
  // both the count + the per-severity badges update together.
  const criticalCount = filteredAlerts.filter(a => a.severity === 'critical').length;
  const warningCount = filteredAlerts.filter(a => a.severity === 'warning').length;

  // Tasks: pending/in-progress only.
  // Phase 989 — Flat list sorted by nearest date first so users can scan
  // upcoming work chronologically. Each row surfaces the building name +
  // due date directly (no per-building grouping accordion).
  const pendingTasks = tasks
    .filter(t => t.status !== 'completed')
    .slice()
    .sort((a, b) => {
      const da = a.due_date || a.start_date || a.reminder_date || '9999-12-31';
      const db = b.due_date || b.start_date || b.reminder_date || '9999-12-31';
      if (da === db) return (a.building_name || '').localeCompare(b.building_name || '');
      return da < db ? -1 : 1;
    });
  // Notes: latest 10, each already carries `building_name` from the API.
  const recentNotes = notes.slice(0, 10);

  // Phase 989 — Email Center building filter (Building Management dashboard).
  // `emailBldgFilter` = '' → All Buildings; else a specific building_id.
  // `emailBldgSearch` = free-text search over the building name so users
  // can quickly locate a building without opening the dropdown.
  const emailBldgSearch = emailFilter.q;
  const emailBldgFilter = emailFilter.building_id;
  const buildingOptions = [
    ...new Map(
      (ops.emails || []).map(e => [e.building_id, {
        building_id: e.building_id,
        building_name: e.building_name || '—',
      }])
    ).values(),
  ].sort((a, b) => (a.building_name || '').localeCompare(b.building_name || ''));
  const filteredEmailRows = (ops.emails || []).filter(row => {
    if (emailBldgFilter && row.building_id !== emailBldgFilter) return false;
    if (emailBldgSearch) {
      const q = emailBldgSearch.trim().toLowerCase();
      if (q && !((row.building_name || '').toLowerCase().includes(q))) return false;
    }
    return true;
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-zinc-500">{t('loading')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="dashboard-page" className="max-w-[1600px]">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-semibold text-zinc-900 tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
            {t('buildingManagement')}
          </h1>
          <p className="text-zinc-500 text-sm mt-1">{t('buildingDashboardDesc')}</p>
        </div>

        {/* ===== TOP: KPI Summary Cards ===== */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <Link to="/buildings" className="bg-white border border-zinc-200 px-5 py-4 hover:border-zinc-300 transition-colors" data-testid="stat-total-buildings">
            <p className="text-zinc-500 text-[11px] uppercase tracking-wider font-medium">{t('totalBuildings')}</p>
            <p className="text-2xl font-semibold text-zinc-900 mt-1 tabular-nums" style={{ fontFamily: 'var(--font-heading)' }}>{stats?.total_buildings || 0}</p>
          </Link>
          <Link to="/buildings" className="bg-white border border-zinc-200 px-5 py-4 hover:border-zinc-300 transition-colors" data-testid="stat-total-units">
            <p className="text-zinc-500 text-[11px] uppercase tracking-wider font-medium">{t('totalUnits')}</p>
            <p className="text-2xl font-semibold text-zinc-900 mt-1 tabular-nums" style={{ fontFamily: 'var(--font-heading)' }}>{stats?.total_units || 0}</p>
          </Link>
          <div className="bg-white border border-zinc-200 px-5 py-4" data-testid="stat-total-outstanding">
            <p className="text-zinc-500 text-[11px] uppercase tracking-wider font-medium">{t('totalOutstanding')}</p>
            <p className={`text-2xl font-semibold mt-1 tabular-nums font-mono ${((bldgData?.total_outstanding || 0) > 0) ? 'text-red-600' : 'text-zinc-900'}`}>
              &euro;{(bldgData?.total_outstanding || 0).toFixed(2)}
            </p>
          </div>
          {/* Phase 209s — Click the Active Alerts card to jump to the
              filtered alerts list below; the count + critical/warning
              badges always reflect the SAME filteredAlerts the list uses. */}
          <a
            href="#operations-widgets"
            onClick={(e) => {
              e.preventDefault();
              const el = document.querySelector('[data-testid="operations-widgets"]');
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            className="bg-white border border-zinc-200 px-5 py-4 hover:border-zinc-300 transition-colors block"
            data-testid="stat-total-alerts"
          >
            <p className="text-zinc-500 text-[11px] uppercase tracking-wider font-medium">{t('activeAlerts')}</p>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <p className="text-2xl font-semibold text-zinc-900 tabular-nums" style={{ fontFamily: 'var(--font-heading)' }} data-testid="active-alerts-count">{filteredAlerts.length}</p>
              {criticalCount > 0 && (
                <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-red-50 text-red-700 border border-red-200 rounded-sm">{criticalCount} {t('critical')}</span>
              )}
              {warningCount > 0 && criticalCount === 0 && (
                <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-sm">{warningCount}</span>
              )}
              {alertTypeFilter !== 'all' && (
                <span className="text-[10px] font-medium px-1.5 py-0.5 bg-zinc-50 text-zinc-700 border border-zinc-200 rounded-sm" data-testid="active-alerts-filter-badge">
                  {(alertTypeOptions.find(o => o.value === alertTypeFilter) || {}).label}
                </span>
              )}
            </div>
          </a>
        </div>

        {/* Phase 990 — Tasks and Notes have been moved to the BOTTOM of
            the dashboard (below Payments by Category). Statement Tracking
            takes their previous position + a new Statement Tracking section
            appears between Operations widgets and Payments by Category. */}

        {/* ===== ROW 3: OPERATIONS DASHBOARD =====
            Phase 989 — Management widget REMOVED (kept underlying data / module
            intact via Payments by Category → code 201). Layout is now
            `Email Center | Next Meetings`. Email Center gains a per-building
            search + dropdown filter so users can drill into a specific
            building's email history without leaving the dashboard. */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-6" data-testid="operations-widgets">
          {/* Phase 989 — Email Center wrapped with a filter toolbar (building
              search + dropdown). The filter mutates only what's passed to
              OperationsWidget, never the underlying `ops.emails`. */}
          <div className="bg-white border border-zinc-200" data-testid="email-center-wrapper">
            <div className="px-3 py-2 border-b border-zinc-100 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <input
                type="text"
                value={emailFilter.q}
                onChange={(e) => setEmailFilter(f => ({ ...f, q: e.target.value }))}
                placeholder={t('searchByBuilding') || 'Search by building name…'}
                className="flex-1 rounded-none border border-zinc-200 px-2 py-1 text-xs bg-white focus:outline-none focus:border-zinc-500"
                data-testid="email-center-search-input"
              />
              <select
                value={emailFilter.building_id}
                onChange={(e) => setEmailFilter(f => ({ ...f, building_id: e.target.value }))}
                className="rounded-none border border-zinc-200 px-2 py-1 text-xs bg-white focus:outline-none focus:border-zinc-500 sm:w-56"
                data-testid="email-center-building-select"
              >
                <option value="">{t('allBuildings') || 'All Buildings'}</option>
                {buildingOptions.map(b => (
                  <option key={b.building_id} value={b.building_id}>{b.building_name}</option>
                ))}
              </select>
              {(emailFilter.q || emailFilter.building_id) && (
                <button
                  type="button"
                  onClick={() => setEmailFilter({ q: '', building_id: '' })}
                  className="text-[10px] uppercase tracking-wider px-2 py-1 border border-zinc-300 text-zinc-600 hover:bg-zinc-50 whitespace-nowrap"
                  data-testid="email-center-clear-filter"
                >{t('clearFilter') || 'Clear'}</button>
              )}
            </div>
            <OperationsWidget
              title={t('emailCenter') || 'Email Center'}
              Icon={Envelope}
              rows={filteredEmailRows}
              buildingLinkTab="emails"
              emptyLabel={t('noEmails') || 'No emails sent yet'}
              testIdBase="ops-emails"
              renderRow={(row) => (
                <span className="text-[11px] text-zinc-600 font-mono flex items-center gap-2" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {row.last_sent_at ? (
                    <>
                      <span className="text-zinc-500 truncate max-w-[150px] hidden md:inline" title={row.subject}>{row.subject || '—'}</span>
                      <span className="uppercase text-[9px] tracking-wider text-zinc-500 font-semibold bg-zinc-100 px-1 py-0.5">{row.email_type}</span>
                      <span className="text-zinc-500">{row.recipient_count}👥</span>
                      <span className="font-semibold text-zinc-800">{formatDateDMY(row.last_sent_at?.slice(0, 10))}</span>
                      {typeof row.days_since === 'number' && (
                        <span className={row.days_since >= 60 ? 'text-red-600' : row.days_since >= 30 ? 'text-amber-600' : 'text-emerald-600'}>
                          {row.days_since}d
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-zinc-400 italic">{t('none') || 'Never sent'}</span>
                  )}
                </span>
              )}
            />
          </div>

          <OperationsWidget
            title={t('nextMeetings') || 'Next Meetings'}
            Icon={UsersThree}
            rows={ops.next_meetings}
            buildingLinkTab="meetings"
            emptyLabel={t('noNextMeetings') || 'No upcoming meetings'}
            testIdBase="ops-meetings"
            renderRow={(row) => (
              <span className="text-[11px] text-zinc-600 font-mono flex items-center gap-2" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {row.next_meeting_date ? (
                  <>
                    <span className="font-semibold text-zinc-800">{formatDateDMY(row.next_meeting_date)}</span>
                    {row.time && <span className="text-zinc-500">{row.time}</span>}
                    {typeof row.days_left === 'number' && (
                      <span className={row.days_left < 0 ? 'text-red-600' : row.days_left <= 7 ? 'text-amber-600' : 'text-emerald-600'}>
                        {row.days_left < 0 ? `${Math.abs(row.days_left)}d overdue` : `${row.days_left}d`}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-zinc-400 italic">{t('none') || 'Not scheduled'}</span>
                )}
              </span>
            )}
          />
        </div>

        {/* ===== ROW 4: PAYMENTS BY CATEGORY (Phase 205 / 951) =====
            Fully user-driven category selection. All available Monthly
            (201-206) and Capital (301-310) expense categories are listed
            in the Categories popover; the user picks any subset. Only
            fully-managed buildings appear (backend filters cleaning_only).
        */}
        {(coverage?.buildings || []).length > 0 && (() => {
          // Phase 951 — Categories catalog comes straight from the backend
          // (Monthly Expenses + Capital Expenses, same codes as the Expenses
          // module). If the API is old or missing, degrade gracefully.
          const CAT_CATALOG = coverage.categories || {};
          const catKeys = Object.keys(CAT_CATALOG).sort();
          // Group monthly / capital for the popover
          const monthlyKeys = catKeys.filter(k => CAT_CATALOG[k]?.group === 'monthly');
          const capitalKeys = catKeys.filter(k => CAT_CATALOG[k]?.group === 'capital');
          const catLabel = (code) => {
            const c = CAT_CATALOG[code];
            if (!c) return code;
            return lang === 'el' ? (c.label_el || c.label) : c.label;
          };
          // Filter user selection down to codes that still exist in the catalog.
          const visibleSet = new Set(pbcVisibleCats.filter(c => catKeys.includes(c)));
          const effectiveCats = catKeys.filter(k => visibleSet.has(k));
          // Fall-back for stale/empty selection: pick first available code.
          const activeCat = effectiveCats.includes(coverageCategory)
            ? coverageCategory
            : (effectiveCats[0] || null);
          // Build rows for selected category
          const rows = activeCat ? (coverage.buildings || []).map(b => {
            const c = b.categories?.[activeCat] || {};
            return {
              building_id: b.building_id,
              building_name: b.building_name,
              last_paid: c.last_paid || '',
              paid_until: c.paid_until || '',
              status: c.status || 'none',
              slot: c,                  // Phase 952 — expose full slot (cert/insurance)
            };
          }) : [];
          // Sort
          const sorted = [...rows].sort((a, b) => {
            if (coverageSort === 'unpaid') {
              const aN = a.last_paid ? 0 : 1;
              const bN = b.last_paid ? 0 : 1;
              if (aN !== bN) return bN - aN;
              const aD = a.last_paid || '9999-12-31';
              const bD = b.last_paid || '9999-12-31';
              return aD.localeCompare(bD);
            }
            const aD = a.last_paid || '9999-12-31';
            const bD = b.last_paid || '9999-12-31';
            const cmp = aD.localeCompare(bD);
            if (cmp !== 0) return cmp;
            return (a.building_name || '').localeCompare(b.building_name || '');
          });

          const toggleCat = (code) => {
            setPbcVisibleCats(prev => (
              prev.includes(code) ? prev.filter(k => k !== code) : [...prev, code]
            ));
          };

          const fmt = (d) => {
            if (!d) return '—';
            const p = d.split('-');
            return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d;
          };

          return (
            <div className="bg-white border border-zinc-200 mb-6" data-testid="payments-by-category-section">
              <div className="px-5 py-3 border-b border-zinc-200 flex items-center gap-2 flex-wrap">
                <Buildings size={16} className="text-zinc-500" />
                <h2 className="font-semibold text-sm text-zinc-900" style={{ fontFamily: 'var(--font-heading)' }}>
                  {t('paymentsByCategory') || 'Payments by Category'}
                </h2>
                <span className="text-[11px] text-zinc-400 font-medium">
                  ({sorted.length})
                </span>
                <div className="ml-auto flex items-center gap-1.5">
                  <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium mr-1">{t('sort') || 'Sort'}</span>
                  <button
                    onClick={() => setCoverageSort('oldest')}
                    className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 border transition-colors ${coverageSort === 'oldest' ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}
                    data-testid="coverage-sort-oldest"
                  >
                    {t('oldestFirst') || 'Oldest first'}
                  </button>
                  <button
                    onClick={() => setCoverageSort('unpaid')}
                    className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 border transition-colors ${coverageSort === 'unpaid' ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}
                    data-testid="coverage-sort-unpaid"
                  >
                    {t('unpaidFirst') || 'Unpaid first'}
                  </button>
                  {/* Phase 951 — Category selection popover */}
                  <div className="relative ml-1">
                    <button
                      onClick={() => setPbcSettingsOpen(o => !o)}
                      className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 border transition-colors flex items-center gap-1 ${pbcSettingsOpen ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}
                      data-testid="pbc-settings-toggle"
                      aria-expanded={pbcSettingsOpen}
                      title={t('manageCategories') || 'Manage categories'}
                    >
                      <Gear size={12} />
                      {t('categories') || 'Categories'}
                    </button>
                    {pbcSettingsOpen && (
                      <>
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setPbcSettingsOpen(false)}
                          data-testid="pbc-settings-backdrop"
                        />
                        <div
                          className="absolute right-0 top-full mt-1 z-20 bg-white border border-zinc-200 shadow-lg min-w-[260px] max-h-[420px] overflow-y-auto"
                          data-testid="pbc-settings-popover"
                        >
                          {/* Monthly Expenses group */}
                          {monthlyKeys.length > 0 && (
                            <>
                              <div className="px-3 py-2 border-b border-zinc-100 bg-zinc-50/60">
                                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                                  {lang === 'el' ? 'Μηνιαία Έξοδα' : 'Monthly Expenses'}
                                </p>
                              </div>
                              <div className="py-1">
                                {monthlyKeys.map(code => {
                                  const checked = visibleSet.has(code);
                                  return (
                                    <label
                                      key={code}
                                      className="flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-zinc-50 transition-colors"
                                      data-testid={`pbc-settings-cat-${code}`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggleCat(code)}
                                        className="w-3.5 h-3.5 accent-zinc-900"
                                      />
                                      <span className="text-zinc-700 font-medium">{catLabel(code)}</span>
                                      <span className="ml-auto text-[9px] text-zinc-400 font-mono">{code}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </>
                          )}
                          {/* Capital Expenses group */}
                          {capitalKeys.length > 0 && (
                            <>
                              <div className="px-3 py-2 border-b border-t border-zinc-100 bg-zinc-50/60">
                                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                                  {lang === 'el' ? 'Κεφαλαιουχικά Έξοδα' : 'Capital Expenses'}
                                </p>
                              </div>
                              <div className="py-1">
                                {capitalKeys.map(code => {
                                  const checked = visibleSet.has(code);
                                  return (
                                    <label
                                      key={code}
                                      className="flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-zinc-50 transition-colors"
                                      data-testid={`pbc-settings-cat-${code}`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggleCat(code)}
                                        className="w-3.5 h-3.5 accent-zinc-900"
                                      />
                                      <span className="text-zinc-700 font-medium">{catLabel(code)}</span>
                                      <span className="ml-auto text-[9px] text-zinc-400 font-mono">{code}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
              {/* Tabs — one per user-selected code, or empty-state prompt */}
              {effectiveCats.length === 0 ? (
                <div className="px-5 py-10 text-center text-zinc-500 text-sm" data-testid="pbc-empty-state">
                  <p className="mb-2">{lang === 'el' ? 'Δεν έχεις επιλέξει κατηγορίες.' : 'No categories selected.'}</p>
                  <button
                    onClick={() => setPbcSettingsOpen(true)}
                    className="text-[11px] uppercase tracking-wider font-semibold px-3 py-1.5 border border-zinc-300 hover:bg-zinc-50 transition-colors"
                    data-testid="pbc-empty-choose"
                  >
                    {lang === 'el' ? 'Επιλογή κατηγοριών' : 'Choose categories'}
                  </button>
                </div>
              ) : (
                <div className="flex border-b border-zinc-200 bg-zinc-50/40 overflow-x-auto">
                  {effectiveCats.map(code => (
                    <button
                      key={code}
                      onClick={() => setCoverageCategory(code)}
                      className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider border-r border-zinc-200 last:border-r-0 transition-colors whitespace-nowrap ${
                        activeCat === code
                          ? 'bg-white text-zinc-900 border-b-2 border-b-zinc-900 -mb-px'
                          : 'text-zinc-500 hover:bg-white hover:text-zinc-800'
                      }`}
                      data-testid={`coverage-tab-${code}`}
                    >
                      {catLabel(code)}
                    </button>
                  ))}
                </div>
              )}
              {/* Table */}
              {/* Table — Phase 952 renders specialized columns for 310
                  (Elevator Certification) and 306 (Building Insurance). */}
              <div className="overflow-x-auto">
                {activeCat === '310' ? (
                  <table className="w-full text-sm" data-testid="pbc-table-cert">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold border-b border-zinc-100">
                        <th className="text-left px-4 py-2">{t('building') || 'Building'}</th>
                        <th className="text-left px-3 py-2 w-40">{lang === 'el' ? 'Τελευταία Επιθεώρηση' : 'Last Inspection'}</th>
                        <th className="text-left px-3 py-2 w-40">{lang === 'el' ? 'Επανεξέταση' : 'Re-inspection'}</th>
                        <th className="text-left px-3 py-2 w-24">{lang === 'el' ? 'Αποτέλεσμα' : 'Result'}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {sorted.map(r => {
                        const cert = r.slot?.cert;
                        const result = (cert?.result || '').toLowerCase();
                        return (
                          <tr key={r.building_id} className="hover:bg-zinc-50/50" data-testid={`pbc-row-${r.building_id}`}>
                            <td className="px-4 py-1.5">
                              <Link to={`/buildings/${r.building_id}`} className="font-medium text-sm text-zinc-900 hover:underline">
                                {r.building_name}
                              </Link>
                            </td>
                            <td className="px-3 py-1.5 font-mono text-xs text-zinc-700">{fmt(cert?.last_inspection)}</td>
                            <td className="px-3 py-1.5 font-mono text-xs text-zinc-700">{fmt(cert?.next_inspection)}</td>
                            <td className="px-3 py-1.5">
                              {result === 'passed' ? (
                                <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm">
                                  {lang === 'el' ? 'ΠΕΡΑΣΕ' : 'PASSED'}
                                </span>
                              ) : result === 'failed' ? (
                                <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 bg-red-50 text-red-700 border border-red-200 rounded-sm" title={cert?.failure_reason || ''}>
                                  {lang === 'el' ? 'ΑΠΕΤΥΧΕ' : 'FAILED'}
                                </span>
                              ) : (
                                <span className="text-[10px] text-zinc-400">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : activeCat === '306' ? (
                  <table className="w-full text-sm" data-testid="pbc-table-insurance">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold border-b border-zinc-100">
                        <th className="text-left px-4 py-2">{t('building') || 'Building'}</th>
                        <th className="text-left px-3 py-2 w-32">{lang === 'el' ? 'Εταιρεία' : 'Provider'}</th>
                        <th className="text-left px-3 py-2 w-28">{lang === 'el' ? 'Από' : 'From'}</th>
                        <th className="text-left px-3 py-2 w-28">{lang === 'el' ? 'Έως' : 'To'}</th>
                        <th className="text-right px-3 py-2 w-24">{lang === 'el' ? 'Πριμ' : 'Premium'}</th>
                        <th className="text-left px-3 py-2 w-32">{lang === 'el' ? 'Πληρωμή' : 'Payment'}</th>
                        <th className="text-left px-3 py-2 w-32">{lang === 'el' ? 'Τελευταία' : 'Last Paid'}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {sorted.map(r => {
                        const ins = r.slot?.insurance;
                        const toDate = ins?.policy_to || '';
                        const expired = toDate && toDate < (coverage.today || '');
                        return (
                          <tr key={r.building_id} className="hover:bg-zinc-50/50" data-testid={`pbc-row-${r.building_id}`}>
                            <td className="px-4 py-1.5">
                              <Link to={`/buildings/${r.building_id}`} className="font-medium text-sm text-zinc-900 hover:underline">
                                {r.building_name}
                              </Link>
                            </td>
                            <td className="px-3 py-1.5 text-xs text-zinc-700 truncate max-w-[200px]" title={ins?.provider || ''}>{ins?.provider || '—'}</td>
                            <td className="px-3 py-1.5 font-mono text-xs text-zinc-700">{fmt(ins?.policy_from)}</td>
                            <td className={`px-3 py-1.5 font-mono text-xs ${expired ? 'text-red-700 font-semibold' : 'text-zinc-700'}`}>{fmt(ins?.policy_to)}</td>
                            <td className="px-3 py-1.5 font-mono text-xs text-right text-zinc-800 tabular-nums">{ins?.premium ? `€${ins.premium.toFixed(2)}` : '—'}</td>
                            <td className="px-3 py-1.5 text-[11px]">
                              {!ins ? (
                                <span className="text-zinc-400">—</span>
                              ) : ins.cancelled ? (
                                <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 bg-zinc-100 text-zinc-600 border border-zinc-200 rounded-sm">
                                  {lang === 'el' ? 'ΑΚΥΡΩΘΗΚΕ' : 'CANCELLED'}
                                </span>
                              ) : ins.fully_paid ? (
                                <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm">
                                  {lang === 'el' ? 'ΕΞΟΦΛΗΘΗΚΕ' : 'PAID'}
                                </span>
                              ) : ins.payment_mode === 'installments' && ins.num_installments > 0 ? (
                                <span className="inline-flex items-center gap-1">
                                  <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-sm">
                                    {ins.paid_installments}/{ins.num_installments}
                                  </span>
                                  <span className="text-[10px] text-zinc-500">
                                    {lang === 'el' ? `${ins.remaining_installments} έμειναν` : `${ins.remaining_installments} left`}
                                  </span>
                                </span>
                              ) : (
                                <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 bg-red-50 text-red-700 border border-red-200 rounded-sm">
                                  {lang === 'el' ? 'ΑΠΛΗΡΩΤΟ' : 'UNPAID'}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-1.5 font-mono text-xs text-zinc-700">{fmt(ins?.last_payment_date)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold border-b border-zinc-100">
                        <th className="text-left px-4 py-2">{t('building') || 'Building'}</th>
                        <th className="text-left px-3 py-2 w-36">{t('lastPaid') || 'Last Paid'}</th>
                        <th className="text-left px-3 py-2 w-36">{t('paidUntil') || 'Paid Until'}</th>
                        <th className="text-left px-3 py-2 w-28">{t('status')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {sorted.map(r => {
                        const dotColor =
                          r.status === 'expired' ? 'bg-red-500' :
                          r.status === 'expiring' ? 'bg-amber-500' :
                          r.status === 'ok' ? 'bg-emerald-500' :
                          'bg-zinc-300';
                        const statusLabel =
                          r.status === 'expired' ? (t('expired') || 'expired') :
                          r.status === 'expiring' ? '≤15d' :
                          r.status === 'ok' ? 'OK' :
                          '—';
                        return (
                          <tr key={r.building_id} className="hover:bg-zinc-50/50" data-testid={`pbc-row-${r.building_id}`}>
                            <td className="px-4 py-1.5">
                              <Link to={`/buildings/${r.building_id}`} className="font-medium text-sm text-zinc-900 hover:underline">
                                {r.building_name}
                              </Link>
                            </td>
                            <td className="px-3 py-1.5 font-mono text-xs text-zinc-700">{fmt(r.last_paid)}</td>
                            <td className={`px-3 py-1.5 font-mono text-xs ${r.status === 'expired' ? 'text-red-700 font-semibold' : r.status === 'expiring' ? 'text-amber-700 font-semibold' : 'text-zinc-700'}`}>{fmt(r.paid_until)}</td>
                            <td className="px-3 py-1.5">
                              <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase">
                                <span className={`inline-block w-2 h-2 rounded-full ${dotColor}`}></span>
                                <span className="text-zinc-600">{statusLabel}</span>
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          );
        })()}

        {/* ===== BOTTOM: Tasks & Notes (Phase 990 — moved to bottom) =====
            Tasks and Notes remain as TWO SEPARATE sections (Phase 989),
            but sit at the bottom of the Building Management dashboard
            per user request. Each row keeps its `Title/Text · Building
            Name · Date` layout. Existing tasks/notes data untouched. */}
        <div className="grid grid-cols-1 gap-3 mb-6" data-testid="tasks-notes-bottom-wrapper">
          <div className="bg-white border border-zinc-200" data-testid="tasks-section">
            <div className="px-5 py-3 border-b border-zinc-200 flex items-center gap-2">
              <ClipboardText size={16} className="text-zinc-500" />
              <h2 className="font-semibold text-sm text-zinc-900" style={{ fontFamily: 'var(--font-heading)' }}>{t('tasks') || 'Tasks'}</h2>
              {pendingTasks.length > 0 && (
                <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-sm" data-testid="tasks-pending-count">
                  {pendingTasks.length} {t('pending') || 'pending'}
                </span>
              )}
            </div>
            <div className="divide-y divide-zinc-100 max-h-[420px] overflow-y-auto">
              {pendingTasks.length === 0 ? (
                <div className="px-4 py-6 text-center text-zinc-400 text-sm">{t('noTasks') || 'No pending tasks'}</div>
              ) : (
                pendingTasks.map(tk => {
                  const taskDate = tk.due_date || tk.start_date || tk.reminder_date || '';
                  const dueDate = taskDate ? new Date(taskDate) : null;
                  const now = new Date();
                  const isOverdue = dueDate && dueDate < now && !!tk.due_date;
                  const s = (tk.status || 'pending');
                  const isCompleted = s === 'completed';
                  const isInProgress = s === 'in_progress';
                  const isWaiting = s === 'waiting';
                  let pillTone, pillLabel;
                  if (isCompleted)        { pillTone = 'bg-emerald-50 text-emerald-700 border border-emerald-200'; pillLabel = t('completed') || 'Completed'; }
                  else if (isInProgress)  { pillTone = 'bg-zinc-100 text-zinc-700 border border-zinc-200';       pillLabel = t('statusInProgress') || t('inProgress') || 'In Progress'; }
                  else if (isWaiting)     { pillTone = 'bg-amber-50 text-amber-700 border border-amber-200';     pillLabel = t('statusWaiting') || 'Waiting'; }
                  else if (isOverdue)     { pillTone = 'bg-red-50 text-red-700 border border-red-200';           pillLabel = t('overdue') || 'Overdue'; }
                  else                    { pillTone = 'bg-zinc-50 text-zinc-600 border border-zinc-200';       pillLabel = t('statusPending') || t('pending') || 'Pending'; }
                  const bName = tk.building_name || (tk.building_id ? tk.building_id : (lang === 'el' ? 'Χωρίς κτίριο' : 'Unassigned'));
                  return (
                    <div
                      key={tk.task_id}
                      onClick={(e) => { e.stopPropagation(); openTaskDetails(tk); }}
                      className={`px-4 py-2 grid grid-cols-[1fr_auto_auto_auto_auto] items-center gap-3 hover:bg-zinc-50 cursor-pointer transition-colors ${isOverdue && !isCompleted ? 'bg-red-50/30' : ''} ${isCompleted ? 'opacity-70' : ''}`}
                      data-testid={`dash-task-${tk.task_id}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isCompleted ? 'bg-emerald-500' : isOverdue ? 'bg-red-500' : isInProgress ? 'bg-zinc-400' : isWaiting ? 'bg-amber-500' : 'bg-zinc-300'}`} />
                        <p className={`text-xs font-medium truncate ${isCompleted ? 'text-zinc-400 line-through' : 'text-zinc-800'}`} data-testid={`dash-task-title-${tk.task_id}`}>
                          {(tk.title && tk.title.trim()) || (tk.notes && tk.notes.trim()) || (lang === 'el' ? '(χωρίς τίτλο)' : '(untitled)')}
                        </p>
                      </div>
                      <Link
                        to={tk.building_id ? `/buildings/${tk.building_id}` : '#'}
                        onClick={(e) => e.stopPropagation()}
                        className="text-[11px] font-semibold text-zinc-600 hover:text-zinc-900 truncate max-w-[220px] hidden sm:inline"
                        title={bName}
                        data-testid={`dash-task-building-${tk.task_id}`}
                      >{bName}</Link>
                      <span className={`text-[10px] font-mono ${isCompleted ? 'text-zinc-400' : isOverdue ? 'text-red-600 font-semibold' : 'text-zinc-500'} whitespace-nowrap`} data-testid={`dash-task-date-${tk.task_id}`}>
                        {taskDate ? formatDateDMY(taskDate) : '—'}
                      </span>
                      <span className={`text-[9px] font-semibold px-1.5 py-0.5 ${pillTone} whitespace-nowrap`} data-testid={`dash-task-status-${tk.task_id}`}>{pillLabel}</span>
                      {!isCompleted && (
                        <button
                          type="button"
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              await axios.post(`${API}/tasks/${tk.task_id}/quick-update`);
                              fetchData();
                            } catch { /* ignore */ }
                          }}
                          className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 transition-colors"
                          data-testid={`dash-task-complete-${tk.task_id}`}
                          title={t('markAsCompleted') || 'Mark as Completed'}
                        >✓</button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="bg-white border border-zinc-200" data-testid="notes-section">
            <div className="px-5 py-3 border-b border-zinc-200 flex items-center gap-2">
              <NoteBlank size={16} className="text-zinc-500" />
              <h2 className="font-semibold text-sm text-zinc-900" style={{ fontFamily: 'var(--font-heading)' }}>{t('notes') || 'Notes'}</h2>
              {recentNotes.length > 0 && (
                <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-zinc-100 text-zinc-700 border border-zinc-200 rounded-sm" data-testid="notes-count">
                  {recentNotes.length}
                </span>
              )}
            </div>
            <div className="divide-y divide-zinc-100 max-h-[320px] overflow-y-auto">
              {recentNotes.length === 0 ? (
                <div className="px-4 py-6 text-center text-zinc-400 text-sm">{t('noNotes') || 'No notes'}</div>
              ) : (
                recentNotes.map(note => {
                  const noteDate = note.created_at ? (note.created_at.split('T')[0]) : '';
                  const bName = note.building_name || (note.building_id ? note.building_id : (lang === 'el' ? 'Χωρίς κτίριο' : 'Unassigned'));
                  return (
                    <div
                      key={note.note_id || note._id}
                      className="px-4 py-2 grid grid-cols-[1fr_auto_auto] items-center gap-3 hover:bg-zinc-50 transition-colors"
                      data-testid={`dash-note-${note.note_id}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <NoteBlank size={12} className="text-zinc-300 flex-shrink-0" />
                        <p className="text-xs text-zinc-700 truncate" data-testid={`dash-note-text-${note.note_id}`}>{note.text || note.content || '-'}</p>
                      </div>
                      <Link
                        to={note.building_id ? `/buildings/${note.building_id}` : '#'}
                        className="text-[11px] font-semibold text-zinc-600 hover:text-zinc-900 truncate max-w-[220px] hidden sm:inline"
                        title={bName}
                        data-testid={`dash-note-building-${note.note_id}`}
                      >{bName}</Link>
                      <span className="text-[10px] font-mono text-zinc-500 whitespace-nowrap" data-testid={`dash-note-date-${note.note_id}`}>
                        {noteDate ? formatDateDMY(noteDate) : '—'}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Phase 600 — Shared Task Details dialog. Opens directly from any
          Dashboard task row; same backend + same handlers as the
          per-building Tasks panel, so updates flow both ways in real time. */}
      <TaskDetailsDialog
        open={taskDetailsOpen}
        taskId={taskDetailsTask?.task_id}
        initialTask={taskDetailsTask}
        t={t}
        onClose={() => setTaskDetailsOpen(false)}
        onChanged={() => fetchData()}
      />

    </Layout>
  );
}
